<template>
  <div
    v-loading="isVerifying"
    class="cloud-intercom"
  >
    <div class="cloud-intercom__header">
      <img
        src="@/assets/img/login/logo.webp"
        alt="智生活"
      >
    </div>
    <div class="cloud-intercom__body">
      <router-view
        v-bind="currentProps"
        @start-verify="onStartVerify"
        @retry-location="onRetryLocation"
        @go-verification="onGoVerification"
        @go-registration="onGoRegistration"
        @arrived-by-code="onArrivedByCode"
        @arrived-by-form="onArrivedByForm"
        @community-name="communityName = $event"
        @guest-token-expired="onGuestTokenExpired"
        @request-media="onRequestMedia"
      />
    </div>
  </div>
</template>

<script>
import { apiVoip } from '@/api/Voip'
import dayjs from 'dayjs'

/**
 * 將 server 回傳的 expireAtUtc 字串統一解析為 dayjs 物件（UTC 語意）。
 * server 可能回傳不含時區後綴（e.g. "2024-01-01T10:00:00"）或含 Z（"...Z"）的 UTC 時間；
 * 不論哪種格式，都補上 Z 強制 UTC 解析，避免 dayjs 依賴本地時區造成偏差。
 * @param {string} dateStr
 * @returns {dayjs.Dayjs}
 */
function parseUtcExpiry (dateStr) {
  if (!dateStr) return dayjs(0)
  const normalized = /Z$|[+-]\d{2}:\d{2}$/.test(dateStr) ? dateStr : dateStr + 'Z'
  return dayjs(normalized)
}

/** guestToken session 有效期（30 分鐘）。超過此時間視為過期，使用者需重新完成驗證流程。 */
const GUEST_SESSION_TTL_MS = 30 * 60 * 1000

/**
 * 定位取得逾時。未指定 options 時 getCurrentPosition 的 timeout 預設為 Infinity，
 * 定位遲遲不回時兩個 callback 都不會觸發，loading 會永久停住。
 */
const GEOLOCATION_TIMEOUT_MS = 15 * 1000

/**
 * 可接受的座標快取時間。預設值為 0，代表每次都必須重新定位。
 * 後端地理圍欄有 200 公尺容差，30 秒內的位移遠在誤差範圍內，
 * 因此允許沿用裝置上既有的座標，省去重新定位的等待——重載後的自動重試特別受惠。
 */
const GEOLOCATION_MAX_AGE_MS = 30 * 1000

/**
 * 驗證流程的看門狗時間，確保 isVerifying 不會卡在 true。
 * isVerifying 驅動 v-loading，遮罩會蓋住整個元件並攔截點擊，卡住即等同全頁按鈕失效。
 * 驗證流程中有無法保證會結束的等待：$recaptchaLoaded / $recaptcha 沒有 timeout，
 * 腳本載入失敗或請求停滯時 promise 可能永不 settle；使用者也可能對權限提示不做任何回應。
 * GEOLOCATION_TIMEOUT_MS 無法涵蓋這些情況（規範明訂等待授權的時間不計入該 timeout）。
 */
const PERMISSION_WATCHDOG_MS = 20 * 1000

/**
 * 查詢定位權限狀態。
 * @returns {Promise<string>} prompt / granted / denied，查詢不支援時為 unsupported
 */
const readGeoPermissionState = async () => {
  try {
    const status = await navigator.permissions.query({ name: 'geolocation' })
    return status.state
  } catch {
    return 'unsupported'
  }
}

/**
 * 查詢麥克風與攝影機的權限狀態。任一為 denied 即視為 denied，其次為 prompt。
 * @returns {Promise<string>} prompt / granted / denied，查詢不支援時為 unsupported
 */
const readMediaPermissionState = async () => {
  try {
    const [cam, mic] = await Promise.all([
      navigator.permissions.query({ name: 'camera' }),
      navigator.permissions.query({ name: 'microphone' })
    ])
    if (cam.state === 'denied' || mic.state === 'denied') return 'denied'
    if (cam.state === 'prompt' || mic.state === 'prompt') return 'prompt'
    return 'granted'
  } catch {
    return 'unsupported'
  }
}

/**
 * 判定「系統提示是否真的跳出來過」的耗時門檻。
 * 提示沒跳時請求會立刻失敗（實測 iOS 19ms）；使用者實際看到並點擊至少要數百毫秒
 * （實測按下拒絕為 5037ms）。兩者差三個數量級，門檻取在中間不會誤判。
 */
const PROMPT_SHOWN_MIN_MS = 500

/**
 * 權限請求失敗後，判斷「重新載入頁面」是否可能改變結果。
 *
 * 判斷依據是**本次請求有沒有跳出系統提示**，而不是 permissions.query 的回報值：
 * 提示有跳，代表使用者剛對本次請求做了決定，重載改變不了那個決定；
 * 提示沒跳，代表這次失敗不帶任何使用者意思表示——在 iOS 上即為權限決定綁在該次
 * page load 所導致的陳舊狀態，重載是唯一的復原路徑。
 *
 * 不改用 permissions.query 的原因是它會說謊：iOS Safari 實測在「設定已改為允許、
 * 但分頁狀態陳舊」時回報 prompt（提示沒跳、19ms 即失敗），若據此判定「使用者關掉了
 * 彈窗、重載無意義」，就會在最需要重載的情境下不重載。
 *
 * 唯一保留的狀態條件是 denied：Chromium 於持久封鎖時誠實回報 denied，此時提示同樣
 * 不會跳，但重載確實無濟於事——少了這個條件，Android 使用者每點一次就會被整頁重載一次。
 *
 * @param {string} state readGeoPermissionState / readMediaPermissionState 的回傳值
 * @param {number|null} elapsedMs 本次請求從發出到失敗的耗時；
 *   null 代表請求根本沒發出（瀏覽器不支援該 API），這種失敗重載同樣改變不了
 * @returns {boolean} true 代表值得重載後再試一次
 */
const isWorthReloading = (state, elapsedMs) =>
  elapsedMs !== null && elapsedMs < PROMPT_SHOWN_MIN_MS && state !== 'denied'

/**
 * 麥克風與相機是否必須分兩次請求。
 *
 * 三星瀏覽器實測無法處理合併的 { audio, video } 請求：只跳麥克風提示，使用者回答後
 * 既不跳相機提示，promise 也永不 settle，整個流程死在那裡；分兩次請求則完全正常。
 * 其他瀏覽器合併請求只跳一次提示，體驗明顯較好，iOS 更因為第二次呼叫已脫離
 * user gesture 而會直接失敗，因此只對三星瀏覽器分開請求。
 *
 * 這是本流程唯一以 UA 判斷的地方。合併請求會不會卡死，在發出請求之前沒有任何方式可以
 * 偵測——沒有旗標可查，唯一的徵兆就是永不 settle，而那時已經無法復原（連後續請求都會
 * 被排在後面而失效）。因此只能以 UA 圈出已知有缺陷的瀏覽器，待其修復後即可移除。
 *
 * @returns {boolean}
 */
const needsSeparateMediaRequests = () => /SamsungBrowser/i.test(navigator.userAgent)

/** 路由名稱常數，對應各子路由 */
const ROUTE = {
  WELCOME: 'cloudIntercomWelcome',
  BLOCKING: 'cloudIntercomBlocking',
  LOCATION_DENIED: 'cloudIntercomLocationDenied',
  OUT_OF_RANGE: 'cloudIntercomOutOfRange',
  MEDIA_PERMISSION: 'cloudIntercomMediaPermission',
  MEDIA_DENIED: 'cloudIntercomMediaDenied',
  HOME: 'cloudIntercomHome',
  VERIFICATION: 'cloudIntercomVerification',
  REGISTRATION: 'cloudIntercomRegistration',
  CALLEES: 'cloudIntercomCallees',
  TIMEOUT: 'cloudIntercomTimeout'
}

export default {
  name: 'CloudIntercom',
  data () {
    return {
      // 由 RegistrationPage 呼叫 GetOnSiteRegisterFormInfo 後透過 emit 傳入
      communityName: '',
      // ValidateGeolocation 成功後取得，用於後續訪客 API 驗權
      geolocationToken: '',
      // 10 分鐘有效期截止時間（server 回傳 UTC+0 原始值，比較時各自加 8 小時）
      expireAtUtc: null,
      // ArrivedInfo / CreateAndArrived 成功後取得，傳入撥打確認頁
      guestToken: '',
      // 地理驗證 session（GeolocationToken）倒數計時器 handle
      expiryTimer: null,
      // 訪客 session（guestToken）倒數計時器 handle
      guestExpiryTimer: null,
      // 「開始驗證」流程進行中（reCAPTCHA → 定位 → API）
      isVerifying: false,
      // 權限提示看門狗計時器 handle，確保 isVerifying 不會因權限提示被關閉而卡住
      verifyWatchdog: null,
      // getUserMedia 已發出但尚未 settle；期間不得再發出新的請求（見 requestMediaPermission）
      isMediaRequestPending: false
    }
  },
  computed: {
    /** 從路由參數取得 QR Code 內的哨點識別碼 */
    publicCode () {
      return this.$route.params.publicCode
    },
    /** 訪客 session 的 sessionStorage key，以 publicCode 為範圍避免不同哨點互相污染 */
    guestSessionKey () {
      return `ci_guest_${this.publicCode}`
    },
    /** 地理驗證 session 的 sessionStorage key */
    geoSessionKey () {
      return `ci_geo_${this.publicCode}`
    },
    /** 待重載重試標記的 sessionStorage key */
    reloadFlagKey () {
      return `ci_reload_${this.publicCode}`
    },
    /** 依當前子路由決定要傳入子元件的 props */
    currentProps () {
      const base = { communityName: this.communityName }
      const route = this.$route.name
      if (route === ROUTE.VERIFICATION || route === ROUTE.REGISTRATION) {
        return { ...base, geolocationToken: this.geolocationToken }
      }
      if (route === ROUTE.CALLEES) {
        return { ...base, guestToken: this.guestToken }
      }
      return base
    }
  },
  watch: {
    '$route' (to) {
      this.validateRouteEntry(to.name)
    }
  },
  created () {
    // 子元件（CalleesPage、VerificationPage 等）在 CloudIntercom mounted 之前就 mount，
    // 若等到 mounted → checkContractStatus 才設 guestToken，子元件拿到的 prop 會是空字串。
    // 在 created 同步預填 sessionStorage 的值，確保子元件 mount 時 props 已有正確資料。
    this.prefillSessionState()
  },
  async mounted () {
    // checkContractStatus 一旦要求導頁就不可接續重試：router 導航是非同步的（子路由皆為動態載入），
    // 此刻 $route.name 仍停在重載前的頁面，直接接續會在阻擋頁或歡迎頁上觸發驗證流程
    if (await this.checkContractStatus()) {
      this.resumeAfterReload()
    }
  },
  beforeDestroy () {
    this.clearExpiryTimer()
    this.clearGuestExpiryTimer()
    this.endVerifying()
  },
  methods: {
    /**
     * 在 created hook 同步預填 sessionStorage 中的 guestToken / geolocationToken，
     * 確保子元件 mount 時透過 currentProps 拿到正確 props，不必等到 mounted → checkContractStatus。
     * 完整的有效性驗證與導頁邏輯仍由 checkContractStatus 負責。
     */
    prefillSessionState () {
      try {
        const guestRaw = sessionStorage.getItem(this.guestSessionKey)
        if (guestRaw) {
          const { guestToken, communityName, savedAt } = JSON.parse(guestRaw)
          if (guestToken && savedAt && Date.now() - savedAt <= GUEST_SESSION_TTL_MS) {
            this.guestToken = guestToken
            this.communityName = communityName || this.communityName
          }
        }
      } catch { /* ignore */ }

      try {
        const geoRaw = sessionStorage.getItem(this.geoSessionKey)
        if (geoRaw) {
          const { geolocationToken, expireAtUtc } = JSON.parse(geoRaw)
          if (geolocationToken && expireAtUtc && !dayjs().isAfter(parseUtcExpiry(expireAtUtc))) {
            this.geolocationToken = geolocationToken
            this.expireAtUtc = expireAtUtc
          }
        }
      } catch { /* ignore */ }
    },

    /**
     * 進入驗證中狀態並啟動看門狗。
     * 看門狗到期只解除 loading 遮罩，不導頁也不顯示錯誤，因為它無從得知流程是真的卡住
     * 還是使用者仍在權限提示上猶豫：若是後者，稍後按下允許時原本的成功流程仍會正常接續；
     * 若流程確實卡住，畫面也已恢復可操作，能再次點擊按鈕重試。
     */
    beginVerifying () {
      this.isVerifying = true
      clearTimeout(this.verifyWatchdog)
      this.verifyWatchdog = setTimeout(() => {
        this.verifyWatchdog = null
        this.isVerifying = false
      }, PERMISSION_WATCHDOG_MS)
    },

    /** 結束驗證中狀態並停止看門狗，所有離開 loading 的路徑都必須經過此處 */
    endVerifying () {
      clearTimeout(this.verifyWatchdog)
      this.verifyWatchdog = null
      this.isVerifying = false
    },

    /**
     * 導頁至指定子路由，若已在目標路由則略過（避免 NavigationDuplicated 錯誤）。
     * @param {string} routeName - ROUTE 常數之一
     * @param {boolean} [replace=false] - true 為取代當前 history（程式自動重導用），false 為新增 history entry（使用者操作用）
     */
    navigate (routeName, replace = false) {
      if (this.$route.name !== routeName) {
        const target = { name: routeName, params: { publicCode: this.publicCode } }
        replace ? this.$router.replace(target) : this.$router.push(target)
      }
    },

    /**
     * 返回鍵觸發路由切換時，驗證目標頁面的前提條件是否滿足。
     * 若 geo session 或 guest session 已失效，導回歡迎頁。
     * @param {string} routeName
     */
    validateRouteEntry (routeName) {
      const geoRequiredRoutes = [
        ROUTE.HOME, ROUTE.VERIFICATION, ROUTE.REGISTRATION,
        ROUTE.MEDIA_PERMISSION, ROUTE.MEDIA_DENIED
      ]
      if (geoRequiredRoutes.includes(routeName)) {
        const geoExpired = !this.geolocationToken || !this.expireAtUtc ||
          dayjs().isAfter(parseUtcExpiry(this.expireAtUtc))
        if (geoExpired && !this.restoreGeoSession()) {
          this.navigate(ROUTE.WELCOME, true)
        }
      }
      if (routeName === ROUTE.CALLEES && !this.guestToken) {
        if (!this.restoreGuestSession()) {
          this.navigate(ROUTE.WELCOME, true)
        }
      }
    },

    /**
     * 進頁後立即查詢此 QR Code 對應的社區是否擁有雲對講 Air 合約
     * API 失敗時保守地顯示阻擋頁，避免無合約社區誤通
     * @returns {Promise<boolean>} 是否可留在當前路由；false 代表已要求導頁，
     *   呼叫端不得再接續任何依賴當前路由的流程（導航完成前 $route 仍是舊值）
     */
    async checkContractStatus () {
      try {
        const res = await apiVoip.Community.GetStatus({ PublicCode: this.publicCode })
        if (res.data.Community) this.communityName = res.data.Community
        if (!res.data.IsActive) {
          this.navigate(ROUTE.BLOCKING, true)
          return false
        }
        const currentRoute = this.$route.name
        if (currentRoute === ROUTE.CALLEES) {
          if (this.restoreGuestSession()) return true
          this.navigate(ROUTE.WELCOME, true)
          return false
        }
        const geoRequiredRoutes = [
          ROUTE.HOME, ROUTE.VERIFICATION, ROUTE.REGISTRATION,
          ROUTE.MEDIA_PERMISSION, ROUTE.MEDIA_DENIED
        ]
        if (geoRequiredRoutes.includes(currentRoute) && !this.restoreGeoSession()) {
          this.navigate(ROUTE.WELCOME, true)
          return false
        }
        return true
      } catch {
        this.navigate(ROUTE.BLOCKING, true)
        return false
      }
    },

    /** 歡迎頁「開始驗證」按鈕觸發 */
    onStartVerify () {
      this.startLocationVerify(false)
    },

    /**
     * 定位驗證流程進入點。
     * getCurrentPosition 必須留在使用者點擊的同步路徑上：iOS Safari 要求定位提示綁定
     * user gesture，先 await reCAPTCHA 會讓手勢失效而拿不到提示。
     * reCAPTCHA 因此改到取得座標之後才執行，順帶避免 token（有效期約 2 分鐘）
     * 在使用者停留於定位提示期間就先過期。
     * **刻意不開 enableHighAccuracy**：它會要求 GPS 定位，冷啟動動輒數秒到十數秒，
     * 使用者等待明顯過久，且逾時後會被導向「定位權限」的錯誤頁而誤導（權限其實正常）。
     * 後端地理圍欄有 200 公尺容差，WiFi 級的粗定位（數十公尺）已足夠。
     * 誤判超出範圍的代價遠低於全體使用者等待——前者有 OutOfRangePage 可重試，後者無解。
     * @param {boolean} isAfterReload - 是否為重載後的自動重試；true 時失敗不再觸發重載。
     *   此情境由 mounted 觸發而非點擊，已不在 user gesture 內，若權限仍停在「詢問」，
     *   iOS 可能不顯示提示就直接進 error callback，改由訊息引導使用者再按一次。
     */
    startLocationVerify (isAfterReload) {
      this.beginVerifying()
      if (!navigator.geolocation) {
        this.handleGeolocationFailure(isAfterReload, null)
        return
      }
      const startedAt = Date.now()
      navigator.geolocation.getCurrentPosition(
        (position) => this.validateGeolocation(position),
        () => this.handleGeolocationFailure(isAfterReload, Date.now() - startedAt),
        { timeout: GEOLOCATION_TIMEOUT_MS, maximumAge: GEOLOCATION_MAX_AGE_MS }
      )
    },

    /**
     * 定位取得失敗（拒絕、無法定位或逾時）一律導向定位被拒頁，讓使用者依指示重新開啟權限。
     * 已在該頁時 navigate 為 no-op，畫面不會有任何變化，因此改以重載或訊息回應。
     * @param {boolean} isAfterReload - 為 true 代表本次已是重載後的重試，不再重載
     * @param {number|null} elapsedMs - 請求耗時，用於判斷系統提示有沒有跳出來（見 isWorthReloading）
     */
    async handleGeolocationFailure (isAfterReload, elapsedMs) {
      this.endVerifying()
      if (this.$route.name !== ROUTE.LOCATION_DENIED) {
        this.navigate(ROUTE.LOCATION_DENIED)
        return
      }
      if (!isAfterReload && await this.shouldReloadForPermission(readGeoPermissionState, elapsedMs)) return
      this.$message({ type: 'error', message: this.$t('Intercom.CloudIntercom.Common.RetryLocationFailed') })
    },

    /**
     * 取得座標後執行 reCAPTCHA 並將座標送至後端驗證地理圍欄，成功後儲存 token 並啟動倒數計時器
     * server 回傳 UTC 時間（含或不含 Z 後綴），統一由 parseUtcExpiry 正確解析後比較
     * 400005（超出範圍）或其他錯誤皆導向 OUT_OF_RANGE，由使用者決定是否重試
     * @param {GeolocationPosition} position
     */
    async validateGeolocation (position) {
      let recaptchaToken
      try {
        await this.$recaptchaLoaded()
        recaptchaToken = await this.$recaptcha('software_entry_point_geolocation_validation')
      } catch {
        // reCAPTCHA 失敗與地理圍欄無關，不可導向超出範圍頁誤導使用者
        this.endVerifying()
        this.$message({ type: 'error', message: this.$t('Intercom.CloudIntercom.Common.VerificationFailed') })
        return
      }

      try {
        const res = await apiVoip.Community.ValidateGeolocation({
          PublicCode: this.publicCode,
          Latitude: position.coords.latitude,
          Longitude: position.coords.longitude,
          RecaptchaToken: recaptchaToken
        })
        this.expireAtUtc = res.data.ExpireAtUtc
        this.geolocationToken = res.data.GeolocationToken
        this.saveGeoSession()
        this.startExpiryTimer()
        this.$message({ type: 'success', message: this.$t('Intercom.CloudIntercom.Common.VerificationSucceeded') })
        const alreadyGranted = await this.checkMediaPermissions()
        this.navigate(alreadyGranted ? ROUTE.HOME : ROUTE.MEDIA_PERMISSION)
      } catch {
        this.navigate(ROUTE.OUT_OF_RANGE)
      } finally {
        this.endVerifying()
      }
    },

    /**
     * 根據 expireAtUtc 計算剩餘毫秒並啟動 setTimeout
     * 到期時若不在撥打確認頁，自動跳轉至 TimeoutPage
     */
    startExpiryTimer () {
      this.clearExpiryTimer()
      const remaining = parseUtcExpiry(this.expireAtUtc) - dayjs()
      if (remaining <= 0) {
        this.clearGuestSession()
        this.clearGeoSession()
        this.navigate(ROUTE.TIMEOUT, true)
        return
      }
      this.expiryTimer = setTimeout(() => {
        this.clearGuestSession()
        this.clearGeoSession()
        // 已進入撥打確認頁時由該頁面自行處理逾時，不在此重複跳轉
        if (this.$route.name !== ROUTE.CALLEES) {
          this.navigate(ROUTE.TIMEOUT, true)
        }
      }, remaining)
    },

    /** 清除倒數計時器，避免重新驗證或元件銷毀時重複觸發 */
    clearExpiryTimer () {
      if (this.expiryTimer) {
        clearTimeout(this.expiryTimer)
        this.expiryTimer = null
      }
    },

    /** 使用者在超出範圍頁或定位被拒頁選擇重新驗證時，直接原地重跑驗證流程 */
    onRetryLocation () {
      this.startLocationVerify(false)
    },

    /**
     * 判斷是否該以重載來套用權限設定，若是則寫入標記並重新載入頁面。
     *
     * iOS Safari 的權限決定綁在該次 page load：使用者在網站設定改為允許後，
     * 原分頁仍持有舊狀態，不重載就永遠拿不到新權限（已於 iOS 實機確認）。
     * 但 Chromium 的權限變更即時生效，重載毫無幫助，只會讓使用者剛做完選擇就被重載打斷。
     * 因此先由 isWorthReloading 判斷本次失敗是否代表狀態陳舊，只有陳舊時才重載。
     *
     * @param {() => Promise<string>} readState 對應權限的狀態查詢函式
     * @param {number|null} elapsedMs 本次請求耗時，用於判斷系統提示有沒有跳出來
     * @returns {Promise<boolean>} 是否已觸發重載；否則由呼叫端改為顯示訊息
     */
    async shouldReloadForPermission (readState, elapsedMs) {
      if (!isWorthReloading(await readState(), elapsedMs)) return false
      try {
        sessionStorage.setItem(this.reloadFlagKey, '1')
      } catch {
        return false
      }
      window.location.reload()
      return true
    },

    /**
     * 重載後自動接續一次重試，使用者只需按一次按鈕。
     * 標記在重試前就先清除，且只有使用者再次點擊按鈕才會重新進入重載流程，
     * 因此不可能出現無限重載；使用者稍後真的改了設定再按，也能再觸發一次重載。
     * 重試哪一種權限由當前路由決定，不需將類型寫進標記。
     * 呼叫前必須確認 checkContractStatus 沒有要求導頁，否則 $route.name 會是導航前的舊值。
     */
    resumeAfterReload () {
      let isPending = false
      try {
        isPending = sessionStorage.getItem(this.reloadFlagKey) === '1'
        sessionStorage.removeItem(this.reloadFlagKey)
      } catch { /* ignore */ }
      if (!isPending) return

      if (this.$route.name === ROUTE.LOCATION_DENIED) {
        this.startLocationVerify(true)
      } else if (this.$route.name === ROUTE.MEDIA_DENIED) {
        this.requestMediaPermission(true)
      }
    },

    /** 使用者選擇「已登記訪客（有驗證碼）」 */
    onGoVerification () {
      this.navigate(ROUTE.VERIFICATION)
    },

    /** 使用者選擇「現場登記訪客（無驗證碼）」 */
    onGoRegistration () {
      this.navigate(ROUTE.REGISTRATION)
    },

    /**
     * 已登記訪客驗證碼驗證成功後的回調
     * @param {{ guestToken: string }} payload
     */
    onArrivedByCode ({ guestToken }) {
      this.guestToken = guestToken
      this.goToCallees()
    },

    /**
     * 現場訪客登記成功後的回調
     * @param {{ guestToken: string }} payload
     */
    onArrivedByForm ({ guestToken }) {
      this.guestToken = guestToken
      this.goToCallees()
    },

    /**
     * 進入撥打確認頁前再次確認是否逾時，
     * 防止使用者在表單停留超時後才送出
     */
    goToCallees () {
      if (this.expireAtUtc && dayjs().isAfter(parseUtcExpiry(this.expireAtUtc))) {
        this.navigate(ROUTE.TIMEOUT, true)
        return
      }
      this.clearGeoSession()
      this.saveGuestSession()
      this.navigate(ROUTE.CALLEES)
    },

    /**
     * 將 guestToken、communityName 與儲存時間戳寫入 sessionStorage，以 publicCode 為範圍，
     * 確保不同 QR Code 的 session 不互相干擾。
     * 在進入 CalleesPage 前呼叫，使重新整理後仍能恢復至此頁。
     * savedAt 用於 restoreGuestSession / startGuestExpiryTimer 計算剩餘效期。
     */
    saveGuestSession () {
      sessionStorage.setItem(
        this.guestSessionKey,
        JSON.stringify({
          guestToken: this.guestToken,
          communityName: this.communityName,
          savedAt: Date.now()
        })
      )
      this.startGuestExpiryTimer(GUEST_SESSION_TTL_MS)
    },

    /**
     * 清除 sessionStorage 中的訪客 session 並停止計時器。
     * 在 guestToken 失效或倒數到期時呼叫，確保使用者下次進入時從頭驗證。
     */
    clearGuestSession () {
      sessionStorage.removeItem(this.guestSessionKey)
      this.clearGuestExpiryTimer()
    },

    /**
     * 嘗試從 sessionStorage 恢復 guestToken 狀態（不導頁）。
     * 恢復時依 savedAt 計算實際剩餘效期並重新啟動倒數計時器。
     * guestToken 是否仍有效，待 CalleesPage 發起 API 後由回應決定。
     * @returns {boolean} 是否成功恢復 session
     */
    restoreGuestSession () {
      try {
        const raw = sessionStorage.getItem(this.guestSessionKey)
        if (!raw) return false
        const { guestToken, communityName, savedAt } = JSON.parse(raw)
        if (!guestToken) return false
        const elapsed = savedAt ? Date.now() - savedAt : GUEST_SESSION_TTL_MS
        if (elapsed >= GUEST_SESSION_TTL_MS) {
          this.clearGuestSession()
          return false
        }
        this.guestToken = guestToken
        this.communityName = communityName || this.communityName
        this.startGuestExpiryTimer(GUEST_SESSION_TTL_MS - elapsed)
        return true
      } catch {
        return false
      }
    },

    /**
     * 依剩餘毫秒啟動 guestToken 前端倒數計時器。
     * 到期時清除 session 並強制導回歡迎頁，即使後端尚未回傳錯誤也能保護使用者。
     * @param {number} remaining 剩餘毫秒數
     */
    startGuestExpiryTimer (remaining) {
      this.clearGuestExpiryTimer()
      if (remaining <= 0) {
        this.clearGuestSession()
        this.navigate(ROUTE.WELCOME, true)
        return
      }
      this.guestExpiryTimer = setTimeout(() => {
        this.clearGuestSession()
        this.navigate(ROUTE.WELCOME, true)
      }, remaining)
    },

    /** 清除 guestToken 倒數計時器，避免元件銷毀或 session 清除時重複觸發 */
    clearGuestExpiryTimer () {
      if (this.guestExpiryTimer) {
        clearTimeout(this.guestExpiryTimer)
        this.guestExpiryTimer = null
      }
    },

    /**
     * 由 CalleesPage 在偵測到 guestToken 失效時 emit `guest-token-expired` 觸發。
     * 清除 session 並導回歡迎頁，讓使用者重新走完驗證流程。
     */
    onGuestTokenExpired () {
      this.clearGuestSession()
      this.navigate(ROUTE.WELCOME, true)
    },

    /**
     * 查詢麥克風與攝影機是否已授權，用於決定是否跳過 MediaPermissionPage。
     * 查詢不支援時 readMediaPermissionState 回傳 unsupported，此處視為未授權（保守顯示權限頁）。
     * 此處刻意不改用 getUserMedia：這是被動查詢，改用 getUserMedia 會在地理驗證成功後
     * 立刻彈出系統提示而繞過權限說明頁，且此時已不在 user gesture 內，在 iOS 上必定失敗。
     * 需要確定授權結果的請求路徑請改用 requestMediaPermission。
     * @returns {Promise<boolean>}
     */
    async checkMediaPermissions () {
      return await readMediaPermissionState() === 'granted'
    },

    /**
     * 將 geolocationToken 與 expireAtUtc 寫入 sessionStorage，以 publicCode 為範圍。
     * 在地理驗證成功後呼叫，使重新整理後仍能恢復至 HOME 或 MEDIA_PERMISSION。
     */
    saveGeoSession () {
      sessionStorage.setItem(
        this.geoSessionKey,
        JSON.stringify({
          geolocationToken: this.geolocationToken,
          expireAtUtc: this.expireAtUtc
        })
      )
    },

    /**
     * 清除 sessionStorage 中的地理驗證 session。
     * 在 GeolocationToken 到期或使用者進入 CalleesPage 時呼叫。
     */
    clearGeoSession () {
      sessionStorage.removeItem(this.geoSessionKey)
    },

    /**
     * 嘗試從 sessionStorage 恢復地理驗證 session。
     * 若找到且未過期，還原 geolocationToken / expireAtUtc 並啟動倒數計時器。
     * @returns {boolean} 是否成功恢復
     */
    restoreGeoSession () {
      try {
        const raw = sessionStorage.getItem(this.geoSessionKey)
        if (!raw) return false
        const { geolocationToken, expireAtUtc } = JSON.parse(raw)
        if (!geolocationToken || !expireAtUtc) return false
        if (dayjs().isAfter(parseUtcExpiry(expireAtUtc))) {
          this.clearGeoSession()
          return false
        }
        this.geolocationToken = geolocationToken
        this.expireAtUtc = expireAtUtc
        this.startExpiryTimer()
        return true
      } catch {
        return false
      }
    },

    /** MediaPermissionPage 與 MediaDeniedPage 的按鈕共用同一個入口 */
    onRequestMedia () {
      this.requestMediaPermission(false)
    },

    /**
     * 請求（或重新確認）麥克風與相機權限。
     *
     * 直接呼叫 getUserMedia 而非 checkMediaPermissions：WebKit 對 camera／microphone 的
     * Permissions API 支援不完整，且「網站設定改為允許」後 query 仍可能回報 prompt，
     * 會讓重試永遠判定為未授權。getUserMedia 是唯一可靠的授權來源。
     * 由使用者點擊觸發時，呼叫必須留在點擊的同步路徑上，中間不可插入任何 await
     * （含 loading 用的延遲），否則 iOS Safari 會因 user gesture 失效而直接拒絕。
     *
     * 預設以 { audio: true, video: true } 合併請求，只跳一次提示；僅在
     * needsSeparateMediaRequests() 為真的瀏覽器改為分兩次請求（見該函式說明）。
     * 分開請求的第二次呼叫已不在 user gesture 內，因此不能當成通用做法。
     *
     * **併發防護**：getUserMedia 沒有任何 timeout 機制（不像 getCurrentPosition 有 timeout
     * option 可保證 callback 必被呼叫），提示未獲回應時 promise 可以永遠 pending。此時看門狗
     * 會解除 loading 讓畫面恢復可操作，但那個請求仍未決；若放任再次呼叫，新的請求會排在舊的
     * 後面而不會跳出提示，使用者只會看到「按了沒反應」。因此 pending 期間改以訊息回應。
     * 旗標刻意不由看門狗清除——未 settle 就是未決，只有重載能真正清掉。
     *
     * @param {boolean} isAfterReload - 為 true 代表本次已是重載後的重試，不再重載。
     *   此情境沒有 user gesture，若權限仍停在「詢問」會直接失敗，改由訊息引導使用者再按一次。
     */
    async requestMediaPermission (isAfterReload) {
      if (this.isMediaRequestPending) {
        this.$message({ type: 'warning', message: this.$t('Intercom.CloudIntercom.Common.MediaRequestPending') })
        return
      }
      this.isMediaRequestPending = true
      this.beginVerifying()
      const startedAt = Date.now()
      try {
        // 每次都立即停止 track，此處僅用於確認授權，通話時 CallingDialog 會重新取得
        if (needsSeparateMediaRequests()) {
          const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true })
          audioStream.getTracks().forEach(t => t.stop())
          const videoStream = await navigator.mediaDevices.getUserMedia({ video: true })
          videoStream.getTracks().forEach(t => t.stop())
        } else {
          const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true })
          stream.getTracks().forEach(t => t.stop())
        }
        this.navigate(ROUTE.HOME, true)
      } catch {
        await this.handleMediaFailure(isAfterReload, Date.now() - startedAt)
      } finally {
        this.isMediaRequestPending = false
        this.endVerifying()
      }
    },

    /**
     * 媒體權限請求失敗的統一出口，與 handleGeolocationFailure 同一套處置。
     * 尚未在 MediaDeniedPage 時導向該頁；已在該頁則 navigate 為 no-op，改以重載或訊息回應。
     * @param {boolean} isAfterReload - 為 true 代表本次已是重載後的重試，不再重載
     * @param {number} elapsedMs - 請求耗時，用於判斷系統提示有沒有跳出來（見 isWorthReloading）
     */
    async handleMediaFailure (isAfterReload, elapsedMs) {
      if (this.$route.name !== ROUTE.MEDIA_DENIED) {
        this.navigate(ROUTE.MEDIA_DENIED)
        return
      }
      if (!isAfterReload && await this.shouldReloadForPermission(readMediaPermissionState, elapsedMs)) return
      this.$message({ type: 'error', message: this.$t('Intercom.CloudIntercom.Common.RetryMediaFailed') })
    }
  }
}
</script>

<style lang="scss">
.ci-alert {
  width: calc(100vw - 48px);
  max-width: 360px;
}
</style>

<style lang="scss" scoped>
.cloud-intercom {
  display: flex;
  flex-direction: column;
  min-height: 100vh; // fallback for browsers without dvh support
  min-height: 100dvh;
  background-color: #fff;

  &__header {
    position: relative;
    height: 54px;
    background-color: #f9f9f9;
    box-shadow: 0 3px 6px 0 rgba(0, 0, 0, 0.16);
    display: flex;
    align-items: center;
    padding: 0 24px;
    flex-shrink: 0;

    img {
      height: 32px;
    }
  }

  &__body {
    display: flex;
    flex-direction: column;
    height: calc(100% - 54px);
  }
}
</style>
